import { Dayjs } from 'dayjs';
import { fetchRating } from 'fetch/CourierScore';
import { InvoiceLineWithKey } from 'fetch/reportFetch';
import { asyncForEach } from 'utils/array';
import { CourierInvoiceLine } from './hooks';

export const NO_REGION = 'NO_REGION';
export const NO_VEHICLE = 'NO_VEHICLE';

export const isShiftLine = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase().includes('shift');

export const isDelivery = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase().includes('delivery');

export const isDeliveryBonus = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase().includes('delivery_bonus');

export const isConsolidated = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey
    .toLowerCase()
    .split('_')
    .some(word => word === 'consolidated');

export const isMileage = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase() === 'mileage';

export const isInboundLine = (line: InvoiceLineWithKey): boolean =>
  line.description.toLowerCase().includes('inbound');

export const isBackupLine = (line: InvoiceLineWithKey): boolean =>
  line.description.toLowerCase().includes('backup');

export const isExtraWork = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase() === 'extra_work';

export const isMinComp = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase().includes('min_total_compensation');

export const isIdVerification = (line: InvoiceLineWithKey): boolean =>
  line.lineItemKey.toLowerCase().includes('id_verification');

export const isExtraWeekendPrice = (line: InvoiceLineWithKey): boolean =>
  line.description.toLowerCase().includes('extra weekend');

export const isSortingLine = (line: InvoiceLineWithKey): boolean =>
  line.description.toLowerCase().includes('sort') &&
  (isExtraWork(line) || line.lineItemKey.toLowerCase().includes('sort'));

export const isDeviation = (line: InvoiceLineWithKey): boolean =>
  line.description.toLowerCase().includes('deviation');

export const getFirstPeriod = (date: Dayjs): Dayjs[] => {
  const start = date.date(1);
  const end = date.date(14);
  return [start, end];
};

export const getSecondPeriod = (date: Dayjs): Dayjs[] => {
  const start = date.date(15);
  const end = start.endOf('month');
  return [start, end];
};

export const fetchRegionalRatings = async (
  courierId: number | string,
  startDate: Dayjs,
  endDate: Dayjs,
  regions: string[]
): Promise<{
  [region: string]: number;
}> => {
  const courierRatings = {};
  await asyncForEach(regions, async region => {
    const rating = await fetchRating(startDate, endDate, region, courierId);
    courierRatings[region] = rating;
  });
  return courierRatings;
};

// Any integers that end with a closing parenthesis
const minCompThresholdRe = /\d+(?=\))/;
export const extractMinCompThreshold = (
  line: InvoiceLineWithKey
): string | undefined => {
  if (isMinComp(line)) {
    return minCompThresholdRe.exec(line.description)?.[0];
  }
};

export const showDeliveryRateColumn = (reportStartDate: Dayjs) =>
  reportStartDate.isAfter(new Date('2021-10-14'));

export const showIDChecksColumn = (
  invoiceLines: CourierInvoiceLine[]
): boolean =>
  Object.values(invoiceLines).some(line => line.idVerifications.count > 0);
